/* eslint-disable max-lines */
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import { useNavigate } from "react-router-dom"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import rehypeSanitize from "rehype-sanitize"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import { toast } from "sonner"
import "katex/dist/katex.min.css"

import type {
  ActivityMessage,
  ChatAppResponse,
  ChatAppResponseOrError,
  DocumentReferencePreview,
  DocumentVersion,
  GPTModelItem,
  PendingFile,
} from "@/api/models"
import type { Schema } from "hast-util-sanitize"

import { createDocumentApi, updateMessageApi } from "@/api/api"
import { getToken } from "@/api/auth-api"
import { addMemoryApi, addPrismNoteApi } from "@/api/memory"
import { FeedbackDialog } from "@/components/feedback-dialog"
import { personalContentAttribute } from "@/components/feedback-dialog/capture-screen"
import {
  ArrowUpRight,
  Brain,
  Bug,
  CircleStop,
  Globe,
  Lightbulb,
  ListPlus,
  MoreHorizontal,
  RotateCcw,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Workflow,
} from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { CardFooter } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import {
  isExternalUrl,
  isPersonalLibraryCitation,
  isPersonalSourcesContext,
} from "@/features/ai-explorer/utils/personal-search-utils"
import { ArtifactCard } from "@/features/artifacts/components/artifact-card"
import { useArtifactStore } from "@/features/artifacts/stores/artifact-store"
import { identifierFromArtifactHref } from "@/features/artifacts/streaming/artifact-stream-parser"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { openCanvasInSidebar } from "@/features/canvas/utils/canvas-sidebar"
import { Error } from "@/features/chat/components/error"
import { prepareMemoryTextForSubmission } from "@/features/chat/components/system-message-utils"
import { useCitationPreview } from "@/features/chat/contexts/use-citation-preview"
import {
  extractChunkTextForHighlight,
  useFindChunkForCitation,
} from "@/features/chat/hooks/use-chunk-finder"
import { useStableBlocks } from "@/features/chat/hooks/use-stable-blocks"
import { useStableStringArray } from "@/features/chat/hooks/use-stable-string-array"
import { useSystemMessage } from "@/features/chat/hooks/use-system-message"
import { useTypewriterReveal } from "@/features/chat/hooks/use-typewriter-reveal"
import styles from "@/features/chat/styles/answer.module.css"
import {
  dedupeImageActivities,
  isImageFinishedFrame,
  resolveImageSessionFiles,
} from "@/features/chat/utils/image-activities"
import { chatMessageMarkdownSanitizeSchema } from "@/features/chat/utils/markdown-sanitize-schema"
import { transformChatMarkdownUrl } from "@/features/chat/utils/markdown-url-transform"
import { normalizeSandboxLinkSchemes } from "@/features/chat/utils/sandbox-link-sanitize-plugin"
import { WorkflowRunResumeCard } from "@/features/workflow-editor/components/workflow-run-resume-card"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { useStreamingCursor } from "@/hooks/use-streaming-cursor"
import { queryKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"
import { ChatService } from "@/services/chat-service"
import { DocumentService } from "@/services/document-service"
import { ImageCacheService } from "@/services/image-cache-service"
import { SettingsService } from "@/services/settings-service"
import { useStudioAIStore } from "@/stores/studio-ai-store"
import { KNOWN_SESSION_TYPES } from "@/types"
import { hasMemoryCandidates, parseMemoryCandidates } from "@/utils/memory-candidate-parser"
import { findModelById } from "@/utils/model-id-utils"
import { stripMemoryCandidateTags } from "@/utils/prism-memory-utils"
import {
  getResponseContent,
  getResponseContext,
  hasDataPoints,
} from "@/utils/response-format-utils"

import { ActivityMessageHistory } from "./activity-message/activity-message-history"
import { ImageResult } from "./activity-message/image-result"
import { AnalysisPanel } from "./analysis-panel"
import { groupAssistantFiles } from "./assistant-file-grouping-utils"
import { AttachmentCard } from "./attachment-card"
import { CardAwareInlineImage } from "./card-aware-inline-image"
import { type CardImageFiles, CardImageFilesContext } from "./card-image-files-context"
import { Citation, type CitationHandle } from "./citation"
import { CitationInlinePeek } from "./citation-inline-peek"
import { createCitationLinkComponent } from "./citation-link"
import { getCitationType } from "./citation.utils"
import { CodeBlock } from "./code-block"
import { DocumentReferencePreviewCard } from "./document-reference-preview-card"
import { FollowupQuestions } from "./followup-questions"
import { isCardImageSrc } from "./inline-session-image-utils"
import { BlockedExternalImage, NeutralizedLink } from "./neutralized-external"
import { PresentationDeckCard } from "./presentation-deck-card"
import {
  inferPresentationDecksFromFiles,
  normalizePresentationDecks,
  stripPresentationPreviewMarkdown,
} from "./presentation-deck-utils"
import { getPrismTurnKey, isPrismAwaitingSources } from "./prism-retrieval/prism-retrieval-utils"
import { SandboxFileLink } from "./sandbox-file-link"
import { StableBlock } from "./stable-block"
import { SystemMessageActions } from "./system-message-actions"
import { SystemMessageBoundary } from "./system-message-boundary"
import { SystemMessageLayout } from "./system-message-layout"
import { ThinkingPanel } from "./thinking-panel"
import { ActivityType } from "../types/activity-message-types"
import { removeTrailingPartialTag } from "../utils/markdown-partial-tag"
import { isSandboxHref } from "../utils/sandbox-file-link"

type QuickAction = {
  key: string
  label: string
  icon: React.ReactNode
  onSelect: () => void
}

// Typed as a tuple explicitly — inlining this in the rehypePlugins array
// literal below widens it to a plain array and breaks react-markdown's
// Pluggable typing.
const chatSanitizePlugin: [typeof rehypeSanitize, Schema] = [
  rehypeSanitize,
  chatMessageMarkdownSanitizeSchema,
]

type DocumentChangeType = NonNullable<DocumentReferencePreview["change_type"]>
type DisplayDocumentReference = DocumentReferencePreview & {
  change_types?: DocumentChangeType[]
}

const supportedDocumentChangeTypes = new Set<DocumentChangeType>([
  "created",
  "updated",
  "edited",
  "read",
  "searched",
])

function normalizeDocumentChangeType(
  changeType: DocumentReferencePreview["change_type"] | undefined
): DocumentChangeType {
  if (!changeType || !supportedDocumentChangeTypes.has(changeType)) {
    return "updated"
  }

  // Surface a single "Updated" action label in the UI.
  return changeType === "edited" ? "updated" : changeType
}

function mergeDocumentReferencesByDocument(
  references: DocumentReferencePreview[]
): DisplayDocumentReference[] {
  const mergedByDocumentId = new Map<string, DisplayDocumentReference>()

  for (const reference of references) {
    const normalizedChangeType = normalizeDocumentChangeType(reference.change_type)
    const existingReference = mergedByDocumentId.get(reference.document_id)

    if (!existingReference) {
      mergedByDocumentId.set(reference.document_id, {
        ...reference,
        change_type: normalizedChangeType,
        change_types: [normalizedChangeType],
      })
      continue
    }

    const existingChangeTypes =
      existingReference.change_types && existingReference.change_types.length > 0
        ? existingReference.change_types
        : [normalizeDocumentChangeType(existingReference.change_type)]
    const mergedChangeTypes = existingChangeTypes.includes(normalizedChangeType)
      ? existingChangeTypes
      : [...existingChangeTypes, normalizedChangeType]

    mergedByDocumentId.set(reference.document_id, {
      ...existingReference,
      title: reference.title || existingReference.title,
      snippet: reference.snippet || existingReference.snippet,
      change_type: normalizedChangeType,
      change_types: mergedChangeTypes,
      version: reference.version ?? existingReference.version,
      updated_at: reference.updated_at ?? existingReference.updated_at,
    })
  }

  return Array.from(mergedByDocumentId.values())
}

export type SystemMessage = {
  answer: ChatAppResponse | ChatAppResponseOrError
  isStreaming: boolean
  noCitation: boolean
  citationSection?: string
  moduleName?: string
  targetLanguage?: string
  customFileName?: string
  activityMessages?: ActivityMessage[]
  index?: number
  sessionId?: string
  userQuery?: string
  messageId?: string
  sessionType?: string
}

export type SystemMessageProps = SystemMessage & {
  /**
   * Surface-specific actions appended to the answer's action row, on the same
   * line as the icon actions and "Fact check" (hosted agents put "Improve
   * result" here, #3165). Rendered only for a completed answer, like the rest
   * of the row.
   */
  children?: React.ReactNode
  isLastMessage?: boolean
  onFollowupQuestionClicked?: (question: string) => void
  showFollowupQuestions?: boolean
  showCopyRaw?: boolean
  showDownload?: boolean
  showFactCheck?: boolean
  showAnalysisPanelIcon?: boolean
  onMessageUpdate?: () => void
  onBreakOutChat?: (content: string) => void
  onSendFollowUp?: (options: { question: string; forceWebSearch?: boolean }) => void | Promise<void>
  onTryAgain?: (question: string) => void | Promise<void>
  onSwitchModel?: (modelId: string) => void
  availableModels?: GPTModelItem[]
  currentModelId?: string
  allowWebSearchAction?: boolean
  onShareMessage?: (messageId?: string) => void
  /** When true, hides the canvas button (read-only session) */
  isReader?: boolean
  /** HERMES B4: show "Publish as artifact" in the action row (hosted-agent threads). */
  showPublishArtifact?: boolean
  /** #3024: render finished artifacts inline under their card instead of
   *  auto-opening the side panel (hosted-agent chat). */
  inlineArtifactPreviews?: boolean
  onPublishArtifactClicked?: () => void
  /** Optional override for citation click handling. When provided, bypasses default routing. */
  onCitationClick?: (rawCitation: string) => void
  /** When true, suppresses the inline ActivityMessageHistory accordion. Used by
   *  Deep Explorer's Workspace layout, where the activity peek in the right
   *  column is the canonical activity view and the chat-rail collapse would
   *  duplicate it. */
  hideActivityHistory?: boolean
  /** Optional override for the loading placeholder shown by ActivityMessageHistory
   *  when no real activity has arrived yet. Deep Explorer uses this to render
   *  "Awaiting plan approval" while the run is paused on the plan-approval
   *  interrupt — otherwise the default "Thinking…" reads as if the model is
   *  stuck. */
  activityLoadingPlaceholder?: string
}

function isSystemInstruction(content: string) {
  return content.startsWith("developer-instructions:")
}

function WorkspacePlanningFeedback() {
  const { t } = useTranslation()
  return (
    <div
      className="border-border/60 bg-muted/30 text-muted-foreground my-2 flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs"
      role="status"
    >
      <Sparkles aria-hidden className="size-3.5 animate-pulse motion-reduce:animate-none" />
      <span>
        {t("deepExplorer.workspace_chat_planning", {
          defaultValue: "Planning...",
        })}
      </span>
    </div>
  )
}

function getActivitiesFromAnswer(
  answer: ChatAppResponse | ChatAppResponseOrError
): ActivityMessage[] {
  if ("activities" in answer) {
    return answer.activities || []
  }
  return []
}

function getActivityDocumentReferences(activities: ActivityMessage[]): DisplayDocumentReference[] {
  const supportedActivityTypes = new Set([
    "doc_creator_completed",
    "doc_updater_completed",
    "doc_content_reader_completed",
    "doc_internal_search_completed",
  ])
  const references: DocumentReferencePreview[] = []

  for (const activity of activities) {
    if (!supportedActivityTypes.has(activity.activity_type)) {
      continue
    }

    const rawReferences: unknown[] = []
    if (activity.details?.document_reference) {
      rawReferences.push(activity.details.document_reference)
    }
    if (Array.isArray(activity.details?.document_references)) {
      rawReferences.push(...activity.details.document_references)
    }

    for (const rawReference of rawReferences) {
      if (!rawReference || typeof rawReference !== "object") {
        continue
      }

      const candidate = rawReference as Partial<DocumentReferencePreview>
      if (
        typeof candidate.document_id !== "string" ||
        typeof candidate.title !== "string" ||
        typeof candidate.snippet !== "string"
      ) {
        continue
      }

      const normalizedReference: DocumentReferencePreview = {
        document_id: candidate.document_id,
        title: candidate.title,
        snippet: candidate.snippet,
        change_type: normalizeDocumentChangeType(candidate.change_type),
        version: candidate.version,
        updated_at: candidate.updated_at,
      }

      references.push(normalizedReference)
    }
  }

  return mergeDocumentReferencesByDocument(references)
}

// Image jobs whose pixels this page already offered to the localStorage cache.
const attemptedImageCacheJobs = new Set<string>()

type MarkdownHastNode = {
  tagName?: string
  type?: string
  value?: string
  properties?: Record<string, unknown>
  children?: unknown[]
}

function isWhitespaceMarkdownChild(child: unknown): boolean {
  if (typeof child === "string") return child.trim().length === 0
  if (!child || typeof child !== "object") return false
  const node = child as MarkdownHastNode
  if (node.tagName) return false
  if (typeof node.value === "string") return node.value.trim().length === 0
  return node.type === "text"
}

function soleElement(children: unknown[]): MarkdownHastNode | null {
  const meaningful = children.filter((child) => !isWhitespaceMarkdownChild(child))
  if (meaningful.length !== 1) return null
  const only = meaningful[0]
  if (!only || typeof only !== "object") return null
  return only as MarkdownHastNode
}

function markdownChildren(node: unknown): unknown[] {
  if (!node || typeof node !== "object" || !("children" in node)) return []
  const children = (node as { children?: unknown }).children
  return Array.isArray(children) ? children : []
}

/**
 * Src of the only image in this wrapper, or null when it also has text or
 * another element. A paragraph may be a single link that itself only holds
 * the image. Whitespace-only text nodes do not count.
 */
function soleImageSrc(node: unknown, unwrapLink: boolean): string | null {
  const only = soleElement(markdownChildren(node))
  if (!only) return null
  if (only.tagName === "img") {
    const src = only.properties?.src
    return typeof src === "string" ? src : ""
  }
  if (!unwrapLink || only.tagName !== "a") return null
  const nested = soleElement(only.children ?? [])
  if (nested?.tagName !== "img") return null
  const src = nested.properties?.src
  return typeof src === "string" ? src : ""
}

/**
 * Paragraph and link renderers are created inside useMemo, so they cannot
 * call useContext. Card files stay in context: a frozen StableBlock still
 * drops the wrapper when the card's file arrives (#3473).
 */
function HideIfOnlyCardImage({
  children,
  node,
  unwrapLink = false,
}: {
  children: React.ReactNode
  node: unknown
  unwrapLink?: boolean
}) {
  const { files, sessionId } = useContext(CardImageFilesContext)
  const src = soleImageSrc(node, unwrapLink)
  if (src !== null && isCardImageSrc(src, files, sessionId)) return null
  return children
}

function withCardImageGate(
  node: unknown,
  rendered: React.ReactNode,
  unwrapLink = false
): React.ReactNode {
  if (soleImageSrc(node, unwrapLink) === null) return rendered
  return (
    <HideIfOnlyCardImage node={node} unwrapLink={unwrapLink}>
      {rendered}
    </HideIfOnlyCardImage>
  )
}

function SystemMessageInner({
  children,
  isLastMessage = false,
  showCopyRaw = true,
  showDownload = true,
  showFactCheck = true,
  showAnalysisPanelIcon = false,
  showFollowupQuestions,
  onFollowupQuestionClicked,
  onMessageUpdate,
  onBreakOutChat,
  onShareMessage,
  onCitationClick,
  index,
  activityMessages,
  isReader = false,
  hideActivityHistory = false,
  activityLoadingPlaceholder,
  showPublishArtifact = false,
  onPublishArtifactClicked,
  inlineArtifactPreviews = false,
  ...systemProps
}: SystemMessageProps) {
  // All hooks must be called at the top, before any conditional returns
  const prismMemory = usePrismMemory()
  const { instance } = useMsal()
  const analysisTriggerRef = useRef<HTMLButtonElement>(null)
  const citationRef = useRef<CitationHandle>(null)
  const sessionType = systemProps.sessionType || KNOWN_SESSION_TYPES.Explorer
  const { gptModel: selectedModel } = useBaseSettings({ sessionType })
  const {
    onSendFollowUp,
    onTryAgain,
    onSwitchModel,
    allowWebSearchAction,
    availableModels = [],
    currentModelId,
  } = systemProps

  const { openPreview } = useCitationPreview()
  const answerForChunkFinder =
    "error" in systemProps.answer ? undefined : (systemProps.answer as ChatAppResponse)
  const { findChunkForUrlCitation } = useFindChunkForCitation(answerForChunkFinder)
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { routes } = useRoutes()
  const setEditorImageUrl = useStudioAIStore((s) => s.setEditorImageUrl)
  const setCurrentView = useStudioAIStore((s) => s.setCurrentView)

  const studioAiRoute = routes.find((r) => r.component === "studio_ai" && r.enabled)

  const handleOpenInEditor = useCallback(
    (imageUrl: string) => {
      if (!studioAiRoute) return
      setEditorImageUrl(imageUrl)
      setCurrentView("image-editor")
      void navigate(studioAiRoute.path)
    },
    [studioAiRoute, setEditorImageUrl, setCurrentView, navigate]
  )
  const isAnswerCompleted =
    !("error" in systemProps.answer) && Boolean(systemProps.answer.completed)
  const isActivelyStreaming = systemProps.isStreaming && !isAnswerCompleted
  // Steer/cancel (issue #2438): the turn was stopped mid-generation and the
  // partial answer persisted. Terminal (completed stays true) — render an
  // explicit marker for both the live post-cancel state and reloaded history.
  const isAnswerInterrupted =
    !("error" in systemProps.answer) && Boolean((systemProps.answer as ChatAppResponse).interrupted)
  // Issue #2513: a durable terminal turn (`cancelled` or `interrupted`) can
  // land with EMPTY content and ZERO activities (Stop pressed, or process
  // loss, before anything streamed). `termination_reason` distinguishes a
  // deliberate user Stop ("cancelled") from process loss ("interrupted") —
  // see the field's doc comment in api/models.ts for why branching on the
  // literal "interrupted" string is safe without a `durable_turn` guard
  // (legacy can never produce that value).
  const isAnswerProcessInterrupted =
    !("error" in systemProps.answer) &&
    (systemProps.answer as ChatAppResponse).termination_reason === "interrupted"
  // Scopes the empty-content/zero-activity render fix to durable-turn
  // answers only — legacy can reach the identical
  // (interrupted:true, termination_reason:"cancelled", content:"",
  // activities:[]) shape on an early Stop, and its rendering must not change
  // (issue #2513 hard constraint). See `durable_turn`'s doc comment.
  const isDurableTurn =
    !("error" in systemProps.answer) &&
    Boolean((systemProps.answer as ChatAppResponse).durable_turn)
  const stoppedIndicator = isAnswerInterrupted && !isActivelyStreaming && (
    <p
      className="text-muted-foreground mt-2 flex items-center gap-1.5 text-xs"
      data-testid="system-message-interrupted"
    >
      <CircleStop className="size-3.5 shrink-0" />
      {isAnswerProcessInterrupted
        ? t("answer.response_interrupted", "Response interrupted")
        : t("answer.response_stopped", "Response stopped")}
    </p>
  )
  // Prism capture status of a saved answer: a stopped partial (#2513), or a
  // completed answer whose source lineage was incomplete or whose Prism capture
  // is still pending (#3339). Neutral and non-blocking — the answer itself
  // always renders — and it only ever says the answer was NOT captured (or not
  // yet confirmed), never that it was stored.
  const memoryCaptureStatus =
    isAnswerCompleted && !isActivelyStreaming
      ? (systemProps.answer as ChatAppResponse).memory_capture_status
      : undefined
  let captureStatusText: string | undefined
  if (memoryCaptureStatus === "memory_capture_blocked_source") {
    captureStatusText = isAnswerInterrupted
      ? t(
          "answer.memory_capture_blocked_source",
          "Prism could not capture this partial answer because its source information was incomplete."
        )
      : t(
          "answer.memory_capture_blocked_source_answer",
          "Prism did not capture this answer because its source information was incomplete."
        )
  } else if (memoryCaptureStatus === "memory_capture_unconfirmed") {
    captureStatusText = isAnswerInterrupted
      ? t(
          "answer.memory_capture_unconfirmed",
          "Prism capture of this partial answer could not be confirmed."
        )
      : t(
          "answer.memory_capture_unconfirmed_answer",
          "Prism capture of this answer could not be confirmed."
        )
  } else if (memoryCaptureStatus === "memory_capture_pending") {
    captureStatusText = isAnswerInterrupted
      ? t(
          "answer.memory_capture_pending",
          "This partial answer was saved. Prism capture is still pending and has not been confirmed."
        )
      : t(
          "answer.memory_capture_pending_answer",
          "This answer was saved. Prism capture is still pending and has not been confirmed."
        )
  }
  const captureStatusNotice = captureStatusText && (
    <p
      className="text-muted-foreground mt-1 text-xs"
      data-testid="system-message-capture-status"
      role="status"
    >
      {captureStatusText}
    </p>
  )
  const stoppedStatus = (stoppedIndicator || captureStatusNotice) && (
    <div>
      {stoppedIndicator}
      {captureStatusNotice}
    </div>
  )

  // Activities are always in answer.activities - single source of truth
  const allActivities = useMemo(
    () => getActivitiesFromAnswer(systemProps.answer),
    [systemProps.answer]
  )

  // Extract tool approvals from response context for activity message display
  const toolApprovals = useMemo(() => {
    if ("error" in systemProps.answer) {
      return undefined
    }
    const context = getResponseContext(systemProps.answer)
    return context.tool_approvals
  }, [systemProps.answer])

  // Separate image activities to display them prominently in the main response.
  // The backend emits multiple "image_generated" events for one job (50% partial,
  // 75% partial, 100% final) so the user can watch the image build up. We dedupe
  // by job_id and keep the latest entry per id so each render slot updates
  // in-place — React reuses the keyed ImageResult and just swaps the image_url.
  //
  // Once the turn finishes (!isActivelyStreaming), any entry still in partial state
  // is a "ghost" — the backend's stream got cancelled before it could send the
  // final event. If the partial has a usable image, we keep the slot but treat it
  // as final at render time (overlay drops, action buttons appear). If it never
  // produced a URL (stuck 0% placeholder), drop it entirely so we don't leave
  // empty "Generating…" boxes haunting the chat.
  const dedupedImageActivities = useMemo(
    () => dedupeImageActivities(allActivities, isActivelyStreaming),
    [allActivities, isActivelyStreaming]
  )
  const nonImageActivities = useMemo(
    () =>
      allActivities.filter(
        (a) =>
          a.activity_type !== "image_generated" &&
          // The Deep Explorer plan-approval state has its own dedicated UI
          // (the inline "Plan ready — Approve & Run / Review →" bar in
          // ai-explorer-page.tsx), so the redundant spinner row from this
          // backend activity is filtered out at the render boundary.
          // Persisted records still contain it for replay/debugging.
          a.details?.translation_key !== "deep_plan_waiting_approval"
      ),
    [allActivities]
  )
  const activityDocumentReferences = useMemo(() => {
    return getActivityDocumentReferences(allActivities)
  }, [allActivities])

  const rawFileMeta = useMemo(() => {
    const answer = systemProps.answer as ChatAppResponse

    // Prefer top-level fields, but fall back to streaming context updates
    const ctx = answer.context as Partial<ChatAppResponse["context"]>
    const assistant =
      answer?.assistant_files || // eslint-disable-line @typescript-eslint/no-unnecessary-condition
      (ctx as unknown as { assistant_files?: string[] })?.assistant_files || // eslint-disable-line @typescript-eslint/no-unnecessary-condition
      []
    const user =
      answer.user_files || (ctx as unknown as { user_files?: string[] })?.user_files || [] // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    const sessionId = ctx.session_id || systemProps.sessionId || ""
    // Prioritize messageId from answer object, then from context, then from props
    const messageId = answer.messageId || (ctx as unknown as { message_id?: string })?.message_id // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    const fileCreationFailed = !!(ctx as unknown as { file_creation_failed?: boolean })
      ?.file_creation_failed // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    const pendingFiles = (ctx as unknown as { pending_files?: PendingFile[] })?.pending_files ?? [] // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    // Earlier-turn files the answer links to again: download targets only, no cards.
    const linkable = [...assistant, ...(ctx.referenced_files ?? [])]

    return { assistant, user, sessionId, messageId, fileCreationFailed, pendingFiles, linkable }
  }, [systemProps.answer, systemProps.sessionId])

  // Every answer update rebuilds the file arrays (or falls back to a fresh
  // `[]`). Keep them content-stable so `markdownConfig` keeps its identity and
  // frozen blocks don't remount inline images / code blocks (#3485).
  // `fileMeta` itself is a new object on every render: depend on its fields,
  // never on the object.
  const assistantFiles = useStableStringArray(rawFileMeta.assistant)
  const userFiles = useStableStringArray(rawFileMeta.user)
  const linkableFiles = useStableStringArray(rawFileMeta.linkable)
  const fileMeta = {
    ...rawFileMeta,
    assistant: assistantFiles,
    user: userFiles,
    linkable: linkableFiles,
  }

  // An entry whose final frame was lost (a durable turn cancelled during the
  // store step) is named from the message's stored files, see
  // resolveImageSessionFiles.
  const imageActivities = useMemo(
    () => resolveImageSessionFiles(dedupedImageActivities, fileMeta.assistant),
    [dedupedImageActivities, fileMeta.assistant]
  )
  // Files this message's image cards already show. The model sometimes embeds
  // the same file inline in its answer; that copy is hidden (#3473). The value
  // only changes when the set of files does, not on every progress frame.
  const cardImageFilesKey = useMemo(
    () =>
      JSON.stringify(
        [
          ...new Set(
            imageActivities
              .map((a) => a.details?.session_file)
              .filter((f): f is string => typeof f === "string" && f.length > 0)
          ),
        ].sort()
      ),
    [imageActivities]
  )
  const cardImageFiles = useMemo<CardImageFiles>(
    () => ({
      files: new Set(JSON.parse(cardImageFilesKey) as string[]),
      sessionId: fileMeta.sessionId || undefined,
    }),
    [cardImageFilesKey, fileMeta.sessionId]
  )

  const responseContext = getResponseContext(systemProps.answer)
  // A message built from the user's personal sources (mail, OneDrive, chats): text in it may have been
  // written by somebody else, so it can neither link out nor load anything from outside, and it cannot
  // be exported, turned into a document or shared (the server refuses those as well). Derived before the
  // image-cache effect so a personal answer never writes finished pixels to localStorage.
  const isPersonalMessage = isPersonalSourcesContext(responseContext)

  // Cache image activities for the same page until a reload. Writing the
  // cache serialises the whole cache to localStorage, so write once per image
  // per page (the message remounts when its id arrives), and only for finished
  // pixels: the final entry, or a 100% frame (the pixels are done; the store
  // step may still be running or have been cut short). Images above the
  // cache's budget are refused; those are served from the stored session file
  // after a reload. A personal message never writes, and drops a job that was
  // cached before the marker arrived.
  useEffect(() => {
    imageActivities.forEach((activity) => {
      const details = activity.details
      if (!isImageFinishedFrame(details)) return
      const jobId = details?.job_id as string
      if (!jobId) return
      if (isPersonalMessage) {
        ImageCacheService.removeImage(jobId)
        return
      }
      const imageUrl = details?.image_url as string

      if (imageUrl && imageUrl.length > 0 && !attemptedImageCacheJobs.has(jobId)) {
        // One attempt per job whatever the outcome: a write the cache refuses
        // (an image above its budget) must not be retried on every streamed
        // frame, each one serialising the whole cache.
        attemptedImageCacheJobs.add(jobId)
        ImageCacheService.setImage(jobId, imageUrl)
      }
    })
  }, [imageActivities, isPersonalMessage])

  const {
    answerTextRef,
    parsedAnswer,
    documents,
    citationSection,
    followupQuestions,
    handleCopyRawToClipboardClicked,
    handleCopyToClipboardClicked,
    handleDownloadClicked,
    documentReferences,
  } = useSystemMessage({ ...systemProps, isStreaming: isActivelyStreaming })

  const presentationDecks = useMemo(() => {
    const ctx = getResponseContext(systemProps.answer) as Record<string, unknown>
    const decks = normalizePresentationDecks(ctx.presentation_decks)
    return decks.length > 0 ? decks : inferPresentationDecksFromFiles(fileMeta.assistant)
  }, [fileMeta.assistant, systemProps.answer])

  const answerHtml = useMemo(() => {
    const html = stripPresentationPreviewMarkdown(parsedAnswer.answerHtml, presentationDecks)
    return prismMemory ? stripMemoryCandidateTags(html) : html
  }, [parsedAnswer.answerHtml, presentationDecks, prismMemory])

  const revealedHtml = useTypewriterReveal(answerHtml, isActivelyStreaming)
  const { frozenBlocks, activeTail } = useStableBlocks(revealedHtml, isActivelyStreaming)
  const { cursorStyle } = useStreamingCursor()

  const handleCitationNumberClicked = useCallback(
    (citationIndex: number) => {
      const citation = parsedAnswer.citations[citationIndex]
      if (!citation) {
        return
      }

      if (onCitationClick) {
        onCitationClick(citation)
        return
      }

      if (getCitationType(citation) !== "library" || isPersonalLibraryCitation(citation)) {
        citationRef.current?.scrollToReferenceList(citationIndex)
        return
      }

      const chunk = findChunkForUrlCitation(citation)
      const chunkText = extractChunkTextForHighlight(chunk, citation)
      openPreview(citation, citationSection ?? "", {
        chunkText: chunkText ?? undefined,
        documents,
      })
    },
    [
      citationSection,
      documents,
      findChunkForUrlCitation,
      onCitationClick,
      openPreview,
      parsedAnswer.citations,
    ]
  )

  // Prism (#3261) counts the answer's non-FILTH sources without naming them;
  // its line opens the answer's own citation list, which is mounted only once
  // the turn has stopped streaming.
  const handlePrismOtherCitationsClicked = useCallback((citationIndex: number) => {
    citationRef.current?.scrollToReferenceList(citationIndex)
  }, [])
  // Prism follows the turn rather than the stream (#3261): the answer's
  // documents arrive after the text, with `message_saved`, which also
  // remounts this message. The key carries Prism across that remount, and
  // Prism stays live until the documents name the cited sources.
  const prismTurnKey = getPrismTurnKey(systemProps.sessionId, index)
  const prismAwaitingSources = isPrismAwaitingSources({
    inStreamingSlot: systemProps.isStreaming,
    answerCompleted: isAnswerCompleted,
    citations: parsedAnswer.citations,
    documents,
  })

  const handleAnswerClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement
      const button = target.closest(".supContainer")
      if (button) {
        const rawCitation = button.getAttribute("data-citation-raw")
        const citationIndex = button.getAttribute("data-citation-index")
        if (rawCitation && onCitationClick && citationIndex === null) {
          onCitationClick(rawCitation)
          return
        }

        if (citationIndex !== null) {
          handleCitationNumberClicked(parseInt(citationIndex, 10))
        }
      }
    },
    [handleCitationNumberClicked, onCitationClick]
  )

  const visibleDocumentReferences = useMemo<DisplayDocumentReference[]>(() => {
    if (activityDocumentReferences.length > 0) {
      return activityDocumentReferences
    }
    return mergeDocumentReferencesByDocument(documentReferences ?? [])
  }, [activityDocumentReferences, documentReferences])
  const [isResponseIssueDialogOpen, setIsResponseIssueDialogOpen] = useState(false)

  const presentationPreviewFiles = useMemo(
    () => new Set(presentationDecks.flatMap((deck) => deck.previewImages)),
    [presentationDecks]
  )
  const assistantFilesForAttachments = useMemo(
    () => fileMeta.assistant.filter((filename) => !presentationPreviewFiles.has(filename)),
    [fileMeta.assistant, presentationPreviewFiles]
  )

  const { primary: primaryFiles, secondary: secondaryFiles } = useMemo(
    () => groupAssistantFiles(assistantFilesForAttachments),
    [assistantFilesForAttachments]
  )

  const inlineFiles = useMemo(() => {
    return {
      filenames: [...fileMeta.assistant, ...fileMeta.user],
      sessionId: fileMeta.sessionId,
    }
  }, [fileMeta.assistant, fileMeta.sessionId, fileMeta.user])

  const messageId = systemProps.messageId || fileMeta.messageId

  // Artifact ingestion — feed raw content (with <artifact> tags) into the store
  // so ArtifactCard can read and open it.
  //
  // Key strategy: always use `${sessionId}:${index}` — never the real messageId.
  // Reasons:
  //   1. Stable across streaming: the real messageId can arrive mid-stream,
  //      which would change the key and split a single message into two Map
  //      entries, causing rebuild() to produce a duplicate version.
  //   2. Mount-independent: the component may remount with a different
  //      messageId prop while the underlying message is the same slot.
  //   3. Session-scoped: the artifact store now uses the sessionId to detect
  //      session switches and clear stale data automatically.
  const ingestMessage = useArtifactStore((s) => s.ingestMessage)
  // Artifacts are Explorer-only by design: only the Explorer approach adds the
  // <artifacts> prompt section, so other pages (e.g. Video Insight, which uses a
  // separate VideoInsightChat approach) never emit <artifact> tags. We therefore
  // require both sessionId and index and do NOT fall back to messageId — the
  // session-scoped `${sessionId}:${index}` key must stay stable across streaming
  // and remounts (see reasons above).
  const stableArtifactMessageId =
    systemProps.sessionId !== undefined && index !== undefined
      ? `${systemProps.sessionId}:${String(index)}`
      : undefined
  const pruneMessagesBeyond = useArtifactStore((s) => s.pruneMessagesBeyond)
  useEffect(() => {
    if (!stableArtifactMessageId) return
    // Prune BEFORE the content check: when this is the last message in the thread,
    // drop any orphaned `${sessionId}:${index}` entries beyond it (turns that were
    // deleted or reordered out of the thread). This must run even if THIS final
    // turn is an error or has no body — otherwise a stale artifact would survive
    // whenever the thread ends on an empty message.
    if (isLastMessage && systemProps.sessionId !== undefined && index !== undefined) {
      pruneMessagesBeyond(systemProps.sessionId, index)
    }
    const raw = "error" in systemProps.answer ? "" : getResponseContent(systemProps.answer)
    if (!raw) return
    ingestMessage(stableArtifactMessageId, raw, {
      streaming: isActivelyStreaming,
      sessionId: systemProps.sessionId,
      filenames: inlineFiles.filenames,
    })
  }, [
    stableArtifactMessageId,
    systemProps.answer,
    systemProps.sessionId,
    isActivelyStreaming,
    ingestMessage,
    pruneMessagesBeyond,
    isLastMessage,
    index,
    inlineFiles.filenames,
  ])

  // Determine model label + reasoning effort from response context
  const modelInfo = useMemo(() => {
    if ("error" in systemProps.answer) {
      return { label: "", detail: "", effortLevel: "" }
    }
    const ctxAny = getResponseContext(systemProps.answer) as Record<string, unknown>
    const usageMetadata = ctxAny["usage_metadata"] as Record<string, unknown> | undefined
    // Prefer the last model used in detailed_usage (represents the final/main answer call)
    const detailedUsage = Array.isArray(usageMetadata?.["detailed_usage"])
      ? (usageMetadata["detailed_usage"] as Array<Record<string, unknown>>)
      : []
    const lastDetail =
      detailedUsage.length > 0 ? detailedUsage[detailedUsage.length - 1] : undefined
    const lastModel =
      lastDetail && typeof lastDetail["model"] === "string" ? (lastDetail["model"] as string) : ""

    // Derive a display candidate from context fields with primary preference to lastModel (main answer)
    const displayCandidateRaw =
      lastModel ||
      (typeof ctxAny["model_display_name"] === "string"
        ? (ctxAny["model_display_name"] as string)
        : "") ||
      (typeof ctxAny["model_name"] === "string" ? (ctxAny["model_name"] as string) : "") ||
      (typeof ctxAny["model_id"] === "string" ? (ctxAny["model_id"] as string) : "") ||
      ""

    // Map the raw value to an available model to prefer its display_name.
    // findModelById tolerates a `.`->`-` config typo: the canonical dotted id a
    // response reports (e.g. "gpt-5.4_eu") still resolves even when /api/config
    // carries a stray hyphen variant ("gpt-5-4_eu"), instead of a blank badge.
    const mappedDisplay = findModelById(availableModels, displayCandidateRaw)?.display_name || ""

    // Only show a friendly display name; do not fall back to raw IDs
    const label = (mappedDisplay || "").toString()
    const detail = "" // Do not show technical IDs in tooltip

    // Extract reasoning effort level from feature_usage in context
    const featureUsage = ctxAny["feature_usage"] as Record<string, unknown> | undefined
    const highReasoning = featureUsage?.["high_reasoning"] as Record<string, unknown> | undefined
    const effortLevel =
      typeof highReasoning?.["effort_level"] === "string"
        ? (highReasoning["effort_level"] as string)
        : ""

    return { label, detail, effortLevel }
  }, [availableModels, systemProps.answer])

  // Extract workflow title from response context
  const workflowTitle = useMemo(() => {
    if ("error" in systemProps.answer) {
      return null
    }
    const ctxAny = getResponseContext(systemProps.answer) as Record<string, unknown>
    const title = ctxAny["workflow_title"]
    return typeof title === "string" ? title : null
  }, [systemProps.answer])

  const messageContent = getResponseContent(systemProps.answer)
  // "Share message" needs a saved message: without an id the share dialog falls
  // back to sharing the whole conversation, and the backend rejects an id whose
  // row is not stored yet. A finished answer shows its actions before the save
  // lands (#3482), so wait until it has an id and has left the streaming slot.
  const shareMessageHandler =
    isPersonalMessage || !systemProps.messageId || systemProps.isStreaming
      ? undefined
      : onShareMessage
  const answer = systemProps.answer as ChatAppResponse
  const workflowRun = answer.workflow_run ?? responseContext.workflow_run

  // Create canvas content that includes any generated images as markdown
  // Images can be manually repositioned in the editor, but will be stripped
  // before sending to AI text enhancement APIs (handled in SimpleCanvasDialog)
  const canvasContent = useMemo(() => {
    if (!messageContent && imageActivities.length === 0) return ""

    let content = messageContent || ""

    // Append generated images as markdown
    if (imageActivities.length > 0) {
      const imageMarkdown = imageActivities
        .map((activity) => {
          const jobId = activity.details?.job_id as string
          let imageUrl = activity.details?.image_url as string

          // Try to get from cache if not available directly
          if (!imageUrl && jobId) {
            imageUrl = ImageCacheService.getImage(jobId) || ""
          }

          if (imageUrl) {
            const prompt =
              (activity.details?.revised_prompt as string) ||
              (activity.details?.prompt as string) ||
              "Generated Image"
            return `\n\n![${prompt}](${imageUrl})`
          }
          return ""
        })
        .filter(Boolean)
        .join("")

      content += imageMarkdown
    }

    return content
  }, [messageContent, imageActivities])

  const mapDocumentVersionsToCanvas = useCallback((versions: DocumentVersion[] | undefined) => {
    return (versions ?? []).map((version) => ({
      version: version.version,
      content: version.content,
      timestamp: version.timestamp,
      is_ai_edit: version.edit_source === "ai",
      ai_edit_preview: version.edit_preview,
    }))
  }, [])

  const handleCreateDocumentFromResponse = useCallback(async () => {
    if (!canvasContent.trim()) {
      return
    }

    try {
      const settingsService = SettingsService.getInstance()
      const firstNonEmptyLine = canvasContent
        .split("\n")
        .map((line) => line.trim())
        .find((line) => line.length > 0)
      const documentTitle = (firstNonEmptyLine ?? "")
        .replace(/^#+\s*/, "")
        .slice(0, 80)
        .trim()

      const created = await createDocumentApi(
        {
          title:
            documentTitle ||
            t("documents.from_response_title", {
              defaultValue: "Document from response",
            }),
          content: canvasContent,
          contentFormat: "markdown",
          sourceSessionId: systemProps.sessionId,
          sourceMessageId: systemProps.messageId || fileMeta.messageId || undefined,
        },
        settingsService.token
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })

      openCanvasInSidebar(
        {
          documentId: created.id,
          activeDocumentCanEdit: true,
          sessionId: systemProps.sessionId,
          sessionFilenames: inlineFiles.filenames,
          title: created.title,
          content: created.content || canvasContent,
          versions: mapDocumentVersionsToCanvas(created.versions),
          selectedModelId: selectedModel.id,
          lastSavedAt: created.updated ?? null,
        },
        {
          title: created.title,
        }
      )

      toast.success(
        t("documents.created_from_response", {
          defaultValue: "Document created from response",
        })
      )
    } catch (error) {
      toast.error(
        t("documents.create_failed", {
          defaultValue: "Failed to create document",
        }),
        {
          description: error instanceof window.Error ? error.message : undefined,
        }
      )
    }
  }, [
    canvasContent,
    fileMeta.messageId,
    inlineFiles.filenames,
    selectedModel.id,
    systemProps.messageId,
    systemProps.sessionId,
    t,
    mapDocumentVersionsToCanvas,
    queryClient,
  ])

  const handleOpenDocumentReference = useCallback(
    async (documentId: string) => {
      try {
        const settingsService = SettingsService.getInstance()
        const document = await DocumentService.getDocument(documentId, settingsService.token)

        openCanvasInSidebar(
          {
            documentId: document.id,
            activeDocumentCanEdit: document.currentUserRole !== "reader",
            sessionId: systemProps.sessionId,
            sessionFilenames: inlineFiles.filenames,
            title: document.title,
            content: document.content || "",
            versions: mapDocumentVersionsToCanvas(document.versions),
            selectedModelId: selectedModel.id,
            etag: null,
            lastSavedAt: document.updated ?? null,
          },
          {
            title: document.title,
          }
        )
      } catch (error) {
        toast.error(
          t("documents.open_failed", {
            defaultValue: "Failed to open document",
          }),
          {
            description: error instanceof window.Error ? error.message : undefined,
          }
        )
      }
    },
    [inlineFiles.filenames, mapDocumentVersionsToCanvas, selectedModel.id, systemProps.sessionId, t]
  )

  const handleMermaidBlockUpdate = useCallback(
    async (oldCode: string, newCode: string) => {
      const msgId = systemProps.messageId || fileMeta.messageId
      if (!msgId || !messageContent) {
        throw new window.Error("No message ID or content available")
      }

      const mermaidFencePattern = /(```|~~~)mermaid\s*\n([\s\S]*?)\1/g
      const trimmedOldCode = oldCode.trim()
      const trimmedNewCode = newCode.trim()

      const matchToReplace = Array.from(messageContent.matchAll(mermaidFencePattern)).find(
        (match) => match[2]?.trim() === trimmedOldCode // eslint-disable-line @typescript-eslint/no-unnecessary-condition
      )

      /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
      if (!matchToReplace || matchToReplace.index === undefined) {
        throw new window.Error("Could not find the mermaid block to update")
      }

      const fenceMarker = matchToReplace[1]
      const startIndex = matchToReplace.index
      const endIndex = startIndex + matchToReplace[0].length
      const replacement = `${fenceMarker}mermaid\n${trimmedNewCode}\n${fenceMarker}`
      const updatedContent =
        messageContent.slice(0, startIndex) + replacement + messageContent.slice(endIndex)

      const settingsService = SettingsService.getInstance()
      await updateMessageApi(msgId, updatedContent, settingsService.token, undefined, false)

      if (onMessageUpdate) {
        onMessageUpdate()
      }
    },
    [systemProps.messageId, fileMeta.messageId, messageContent, onMessageUpdate]
  )

  const handleAddToMemory = async () => {
    // Personal answers are not saved into memory: a copy stored without session_id
    // survives withdrawal, because copy purge deletes user_memory only by session_id.
    if (isPersonalMessage) return
    try {
      const tokenResult = await getToken(instance)
      if (messageContent) {
        const { text, wasTruncated } = prepareMemoryTextForSubmission(messageContent)
        if (prismMemory) {
          const result = await addPrismNoteApi(text, fileMeta.sessionId, {
            idToken: tokenResult?.accessToken,
            turnUid: "turn_id" in systemProps.answer ? systemProps.answer.turn_id : undefined,
          })
          if (result.result !== "saved") {
            toast.info(t("personalization.memoryPanel.prism.notApplied"))
            return
          }
        } else {
          await addMemoryApi("stm", { text }, tokenResult?.accessToken)
        }
        toast.success(wasTruncated ? "Added to memory (truncated)" : "Added to memory")
      }
    } catch {
      toast.error("Failed to add to memory")
    }
  }

  const userQuestion = systemProps.userQuery?.trim() ?? ""

  const quickActions = useMemo<QuickAction[]>(() => {
    const actions: QuickAction[] = []

    if (userQuestion && onTryAgain) {
      actions.push({
        key: "try-again",
        label: t("answer.actions.try_again", { defaultValue: "Try again" }),
        icon: <RotateCcw className="size-4" />,
        onSelect: () => {
          void Promise.resolve(onTryAgain(userQuestion))
        },
      })
    }

    if (userQuestion && onSendFollowUp) {
      actions.push({
        key: "add-details",
        label: t("answer.actions.add_details", { defaultValue: "Add details" }),
        icon: <ListPlus className="size-4" />,
        onSelect: () => {
          const prompt = t("answer.actions.add_details_prompt", {
            defaultValue: 'Add more detail to your previous answer about "{{subject}}".',
            subject: userQuestion,
          })
          void Promise.resolve(onSendFollowUp({ question: prompt }))
        },
      })
    }

    if (onSendFollowUp) {
      actions.push({
        key: "more-concise",
        label: t("answer.actions.more_concise", { defaultValue: "More concise" }),
        icon: <Sparkles className="size-4" />,
        onSelect: () => {
          const instruction = userQuestion
            ? t("answer.actions.more_concise_prompt_with_subject", {
                defaultValue:
                  'Rewrite your previous answer about "{{subject}}" to be more concise.',
                subject: userQuestion,
              })
            : t("answer.actions.more_concise_prompt", {
                defaultValue: "Rewrite your previous answer to be more concise.",
              })
          void Promise.resolve(onSendFollowUp({ question: instruction }))
        },
      })

      if (allowWebSearchAction) {
        const subject = userQuestion || messageContent.slice(0, 120) || ""
        const prompt = subject
          ? t("answer.actions.search_web_prompt", {
              defaultValue: "Search the web for the latest information about: {{subject}}",
              subject,
            })
          : t("answer.actions.search_web_generic_prompt", {
              defaultValue:
                "Search the web for additional context or updates on the previous answer.",
            })
        actions.push({
          key: "search-web",
          label: t("answer.actions.search_web", { defaultValue: "Search the web" }),
          icon: <Globe className="size-4" />,
          onSelect: () => {
            void Promise.resolve(onSendFollowUp({ question: prompt, forceWebSearch: true }))
          },
        })
      }
    }

    return actions
  }, [allowWebSearchAction, messageContent, onSendFollowUp, onTryAgain, t, userQuestion])

  const isStreamingRef = useRef(isActivelyStreaming)
  isStreamingRef.current = isActivelyStreaming
  const handleMermaidBlockUpdateRef = useRef(handleMermaidBlockUpdate)
  handleMermaidBlockUpdateRef.current = handleMermaidBlockUpdate
  const citationsRef = useRef(parsedAnswer.citations)
  citationsRef.current = parsedAnswer.citations
  const citationSectionRef = useRef(citationSection)
  citationSectionRef.current = citationSection

  // Only owners can edit mermaid blocks (legacy sessions without currentUserRole are treated as owned)
  const currentSessionFromService = fileMeta.sessionId
    ? ChatService.getInstance().getSessionById(fileMeta.sessionId)
    : null
  const canEditMermaid = currentSessionFromService?.currentUserRole !== "reader"

  // Inline session images cache their finished-turn lookup per turn: the
  // message id once known, else the same `${sessionId}:${index}` slot key the
  // artifact store uses. Keyed per turn so a 404 pinned for one turn cannot
  // hide the same filename registered by a later turn.
  const inlineImageTurnId = messageId || stableArtifactMessageId

  // Shared ReactMarkdown configuration - only recreate when stable identifiers change
  const markdownConfig = useMemo(() => {
    const citationLink = createCitationLinkComponent(
      () => citationsRef.current,
      () => citationSectionRef.current ?? "",
      index
    )
    return {
      rehypePlugins: [rehypeRaw, normalizeSandboxLinkSchemes, chatSanitizePlugin, rehypeKatex],
      remarkPlugins: [remarkGfm, remarkMath],
      urlTransform: transformChatMarkdownUrl,
      components: {
        // An artifact block is replaced by a single [title](artifact:id) link,
        // which markdown wraps in a paragraph. The card (and #3024's inline
        // preview) render block content, and block elements inside <p> are
        // invalid HTML (React hydration warnings) — so a paragraph whose only
        // content is an artifact link is unwrapped to a div.
        p: (props: React.HTMLAttributes<HTMLParagraphElement> & { node?: unknown }) => {
          const node = props.node as
            | { children?: Array<{ tagName?: string; properties?: { href?: string } } | string> }
            | undefined
          const elementChildren = (node?.children ?? []).filter(
            (child): child is { tagName?: string; properties?: { href?: string } } =>
              typeof child === "object" && "tagName" in child
          )
          const onlyArtifactLink =
            elementChildren.length === 1 &&
            elementChildren[0]?.tagName === "a" &&
            Boolean(identifierFromArtifactHref(String(elementChildren[0]?.properties?.href ?? "")))
          const { node: _node, ...paragraphProps } = props
          if (onlyArtifactLink) {
            return <div className={paragraphProps.className}>{paragraphProps.children}</div>
          }
          return withCardImageGate(props.node, <p {...paragraphProps} />, true)
        },
        a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) => {
          if (isPersonalMessage && isExternalUrl(props.href)) {
            return withCardImageGate(
              props.node,
              <NeutralizedLink href={props.href ?? ""}>{props.children}</NeutralizedLink>
            )
          }
          if (isSandboxHref(props.href)) {
            if (isPersonalMessage) {
              return withCardImageGate(props.node, <span>{props.children}</span>)
            }
            return withCardImageGate(
              props.node,
              <SandboxFileLink
                assistantFiles={fileMeta.linkable}
                href={props.href}
                sessionId={fileMeta.sessionId}
              >
                {props.children}
              </SandboxFileLink>
            )
          }
          const artifactIdentifier = identifierFromArtifactHref(props.href)
          if (artifactIdentifier) {
            return withCardImageGate(
              props.node,
              <ArtifactCard
                identifier={artifactIdentifier}
                inlinePreview={inlineArtifactPreviews}
              />
            )
          }
          return withCardImageGate(props.node, citationLink(props))
        },
        code: (props: { inline?: boolean; className?: string; children?: React.ReactNode }) => (
          <CodeBlock
            {...props}
            canEditMermaid={canEditMermaid}
            isStreaming={isStreamingRef.current}
            sessionId={systemProps.sessionId}
            onMermaidBlockUpdate={handleMermaidBlockUpdateRef.current}
          />
        ),
        img: ({ src, alt, ...props }: { src?: string; alt?: string }) =>
          isPersonalMessage && (isExternalUrl(src) || !src) ? (
            <BlockedExternalImage alt={alt} />
          ) : (
            <CardAwareInlineImage
              key={`${inlineFiles.sessionId}::${src}`}
              alt={alt}
              filenames={inlineFiles.filenames}
              isStreaming={isStreamingRef.current}
              sessionId={inlineFiles.sessionId}
              slotTurnId={stableArtifactMessageId || undefined}
              src={src}
              turnId={inlineImageTurnId}
              {...props}
            />
          ),
      },
    }
  }, [
    inlineFiles.filenames,
    inlineFiles.sessionId,
    inlineImageTurnId,
    fileMeta.linkable,
    fileMeta.sessionId,
    systemProps.messageId,
    systemProps.sessionId,
    canEditMermaid,
    inlineArtifactPreviews,
    isPersonalMessage,
  ])

  const hasDropdownActions = useMemo(() => {
    return (
      quickActions.length > 0 ||
      showAnalysisPanelIcon ||
      Boolean(onBreakOutChat) ||
      Boolean(shareMessageHandler) ||
      (Boolean(onSwitchModel) && availableModels.length > 0) ||
      Boolean(messageContent)
    )
  }, [
    quickActions.length,
    showAnalysisPanelIcon,
    onBreakOutChat,
    shareMessageHandler,
    onSwitchModel,
    availableModels.length,
    messageContent,
  ])

  const visiblePendingFiles =
    fileMeta.fileCreationFailed || (isAnswerCompleted && !systemProps.isStreaming)
      ? []
      : fileMeta.pendingFiles.filter(
          (pendingFile) => !fileMeta.assistant.some((filename) => filename === pendingFile.filename)
        )

  const hasFileTerminalState =
    visiblePendingFiles.length > 0 ||
    Boolean(fileMeta.sessionId && presentationDecks.length > 0 && isLastMessage) ||
    Boolean(fileMeta.sessionId && (primaryFiles.length > 0 || secondaryFiles.length > 0)) ||
    fileMeta.fileCreationFailed

  const fileTerminalState = (
    <>
      {visiblePendingFiles.length > 0 && (
        <section className="mt-2 flex flex-col gap-2">
          {visiblePendingFiles.map((pendingFile) => (
            <AttachmentCard
              key={`pf-${pendingFile.file_id}`}
              pending
              filename={pendingFile.filename}
              sessionId=""
            />
          ))}
        </section>
      )}

      {isLastMessage && fileMeta.sessionId && presentationDecks.length > 0 && (
        <section className="mt-2 flex flex-col gap-2">
          {isPersonalMessage
            ? presentationDecks.map((deck) => (
                <span key={`${deck.filename}-${deck.previewImages.join("|")}`} className="text-sm">
                  {deck.filename}
                </span>
              ))
            : presentationDecks.map((deck) => (
                <PresentationDeckCard
                  key={`${deck.filename}-${deck.previewImages.join("|")}`}
                  deck={deck}
                  sessionId={fileMeta.sessionId}
                />
              ))}
        </section>
      )}

      {fileMeta.sessionId && primaryFiles.length > 0 && (
        <details className="mt-2">
          <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-sm select-none">
            {t("chat.deliverableFiles", "Files ({{count}})", {
              count: primaryFiles.length,
            })}
          </summary>
          <section className="mt-2 flex flex-col gap-2">
            {primaryFiles.map((f: string, idx: number) =>
              isPersonalMessage ? (
                <span key={`af-${idx}`} className="text-sm">
                  {f}
                </span>
              ) : (
                <AttachmentCard key={`af-${idx}`} filename={f} sessionId={fileMeta.sessionId} />
              )
            )}
          </section>
        </details>
      )}

      {fileMeta.sessionId && secondaryFiles.length > 0 && (
        <details className="mt-2">
          <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-sm select-none">
            {t("chat.intermediateFiles", "Intermediate files ({{count}})", {
              count: secondaryFiles.length,
            })}
          </summary>
          <section className="mt-2 flex flex-col gap-2">
            {secondaryFiles.map((f: string, idx: number) =>
              isPersonalMessage ? (
                <span key={`sf-${idx}`} className="text-sm">
                  {f}
                </span>
              ) : (
                <AttachmentCard key={`sf-${idx}`} filename={f} sessionId={fileMeta.sessionId} />
              )
            )}
          </section>
        </details>
      )}

      {fileMeta.fileCreationFailed && (
        <p
          aria-atomic="true"
          aria-live="polite"
          className="text-muted-foreground mt-2 text-sm"
          role="status"
        >
          {t(
            "chat.fileCreationFailed",
            "One or more generated files could not be attached. Please try again."
          )}
        </p>
      )}
    </>
  )

  if (messageContent && isSystemInstruction(messageContent)) {
    return null
  }

  if (!messageContent) {
    // Consolidated: always render ActivityMessageHistory during streaming, even
    // with 0 activities. ActivityMessageHistory internally handles the empty
    // case with the typewriter "Tänker..." placeholder. A previous branch used
    // <Chat.Loading /> as a fallback here, which caused a component swap (and
    // a typewriter restart) the moment the first activity arrived.
    //
    // Issue #2513: also render (instead of falling through to `return null`
    // below) for a durable terminal turn with zero activities — e.g. Stop
    // pressed, or process loss, before anything streamed — so the
    // `stoppedIndicator` marker a few lines down actually gets a chance to
    // render instead of the turn silently vanishing with no visual trace at
    // all. Gated on `isDurableTurn` so a legacy turn that reaches the exact
    // same empty shape keeps its exact current (unchanged) behavior.
    if (
      isActivelyStreaming ||
      allActivities.length > 0 ||
      (isDurableTurn && isAnswerInterrupted) ||
      hasFileTerminalState
    ) {
      const preAnswerThinking = (systemProps.answer as ChatAppResponse).thinking ?? ""
      return (
        <SystemMessageLayout
          data-message-id={messageId}
          {...personalContentAttribute(isPersonalMessage)}
        >
          <SystemMessageLayout.Content>
            {!hideActivityHistory && (isActivelyStreaming || allActivities.length > 0) && (
              <ActivityMessageHistory
                citationDocuments={documents}
                citations={parsedAnswer.citations}
                citationSection={citationSection}
                id={index?.toString()}
                isLoading={!systemProps.answer.completed}
                loadingPlaceholder={activityLoadingPlaceholder}
                messages={nonImageActivities}
                prismAwaitingSources={prismAwaitingSources}
                prismTurnKey={prismTurnKey}
                toolApprovals={toolApprovals}
                onCitationClicked={handleCitationNumberClicked}
              />
            )}
            {/* Deep Explorer pre-approval reasoning: rendered as a
                collapsed-by-default thinking panel so the planner's prose
                is visible but can't be mistaken for the final answer. */}
            {preAnswerThinking && (
              <ThinkingPanel
                isActive={isActivelyStreaming}
                isStreaming={isActivelyStreaming}
                thinking={preAnswerThinking}
              />
            )}
            {hideActivityHistory && isActivelyStreaming && !preAnswerThinking && (
              <WorkspacePlanningFeedback />
            )}
            {/* Display images prominently even when there's no text content */}
            {imageActivities.length > 0 && (
              <section className="mb-4 flex flex-col gap-4">
                {imageActivities.map((activity, idx) => {
                  const jobId =
                    typeof activity.details?.job_id === "string"
                      ? activity.details.job_id
                      : undefined

                  // Once the turn isn't actively streaming, force partial=false
                  // so any stuck "Generating…" overlay drops and the regular
                  // download/edit affordances come back.
                  const partial = isActivelyStreaming
                    ? (activity.details?.partial as boolean | undefined)
                    : false
                  return (
                    <ImageResult
                      key={`image-${jobId ?? idx}`}
                      isStreaming={isActivelyStreaming}
                      sessionId={fileMeta.sessionId || undefined}
                      slotTurnId={stableArtifactMessageId || undefined}
                      turnId={inlineImageTurnId}
                      activity={{
                        type: ActivityType.IMAGE_GENERATED,
                        job_id: jobId,
                        image_url: (activity.details?.image_url as string) || "",
                        revised_prompt: activity.details?.revised_prompt as string | undefined,
                        prompt: (activity.details?.prompt as string) || "",
                        is_edit: activity.details?.is_edit as boolean | undefined,
                        progress: activity.details?.progress as number | undefined,
                        partial,
                        session_file: activity.details?.session_file as string | undefined,
                        model_id: activity.details?.model_id as string | undefined,
                        model_label: activity.details?.model_label as string | undefined,
                      }}
                      onOpenInEditor={studioAiRoute ? handleOpenInEditor : undefined}
                    />
                  )
                })}
              </section>
            )}
            {visibleDocumentReferences.length > 0 && (
              <section className="mt-4 flex flex-col gap-2">
                {visibleDocumentReferences.map((reference) => (
                  <DocumentReferencePreviewCard
                    key={reference.document_id}
                    changeTypes={reference.change_types}
                    reference={reference}
                    onOpen={handleOpenDocumentReference}
                  />
                ))}
              </section>
            )}
            {fileTerminalState}
            {stoppedStatus}
          </SystemMessageLayout.Content>
        </SystemMessageLayout>
      )
    }

    return null
  }

  return (
    <SystemMessageLayout
      data-message-id={messageId}
      {...personalContentAttribute(isPersonalMessage)}
    >
      <SystemMessageLayout.Content>
        {!hideActivityHistory && nonImageActivities.length > 0 && (
          <ActivityMessageHistory
            citationDocuments={documents}
            citations={parsedAnswer.citations}
            citationSection={citationSection}
            id={index?.toString()}
            isLoading={!systemProps.answer.completed}
            loadingPlaceholder={activityLoadingPlaceholder}
            messages={nonImageActivities}
            prismAwaitingSources={prismAwaitingSources}
            prismTurnKey={prismTurnKey}
            toolApprovals={toolApprovals}
            onCitationClicked={handleCitationNumberClicked}
            onOtherCitationsClicked={
              isActivelyStreaming ? undefined : handlePrismOtherCitationsClicked
            }
          />
        )}

        <ThinkingPanel
          isActive={isActivelyStreaming && !(systemProps.answer as ChatAppResponse).content}
          isStreaming={isActivelyStreaming && !(systemProps.answer as ChatAppResponse).content}
          thinking={(systemProps.answer as ChatAppResponse).thinking ?? ""}
        />

        <CardImageFilesContext.Provider value={cardImageFiles}>
          <section
            ref={answerTextRef}
            data-testid={`system-message-answer-text-${index}`}
            data-text-selectable="true"
            role="presentation"
            onClick={handleAnswerClick}
          >
            {hasMemoryCandidates(revealedHtml) ? (
              <div
                className={cn(
                  styles.answerText,
                  isActivelyStreaming && styles.streamingAnswerText,
                  "prose dark:prose-invert"
                )}
              >
                {parseMemoryCandidates(revealedHtml).map((part, idx) => {
                  if (typeof part === "string") {
                    return (
                      <ReactMarkdown key={`text-${idx}`} {...markdownConfig}>
                        {part}
                      </ReactMarkdown>
                    )
                  }
                  return <span key={`component-${idx}`}>{part}</span>
                })}
              </div>
            ) : (
              <div
                data-text-selectable="true"
                className={cn(
                  styles.answerText,
                  isActivelyStreaming && styles.streamingAnswerText,
                  "prose dark:prose-invert"
                )}
              >
                {frozenBlocks.map((block, i) => (
                  <StableBlock key={`frozen-${i}`} config={markdownConfig} html={block} />
                ))}
                <ReactMarkdown {...markdownConfig}>
                  {isActivelyStreaming && cursorStyle !== "none"
                    ? removeTrailingPartialTag(activeTail) +
                      `<span class="streaming-${cursorStyle}"></span>`
                    : removeTrailingPartialTag(activeTail)}
                </ReactMarkdown>
              </div>
            )}
          </section>
        </CardImageFilesContext.Provider>
        {stoppedStatus}
        {!isActivelyStreaming && workflowRun?.status === "paused" && (
          <WorkflowRunResumeCard workflowRun={workflowRun} />
        )}
        <CitationInlinePeek
          answerTextRef={answerTextRef}
          citations={parsedAnswer.citations}
          citationSection={citationSection ?? ""}
          disabled={isActivelyStreaming || systemProps.noCitation}
          documents={documents}
          findChunkForUrlCitation={findChunkForUrlCitation}
          onViewDocument={openPreview}
        />

        {visibleDocumentReferences.length > 0 && (
          <section className="mt-4 flex flex-col gap-2">
            {visibleDocumentReferences.map((reference) => (
              <DocumentReferencePreviewCard
                key={reference.document_id}
                changeTypes={reference.change_types}
                reference={reference}
                onOpen={handleOpenDocumentReference}
              />
            ))}
          </section>
        )}

        {/* Display generated images prominently in the main response */}
        {imageActivities.length > 0 && (
          <section className="mt-4 flex flex-col gap-4">
            {imageActivities.map((activity, idx) => {
              const jobId =
                typeof activity.details?.job_id === "string" ? activity.details.job_id : undefined
              const partial = isActivelyStreaming
                ? (activity.details?.partial as boolean | undefined)
                : false
              return (
                <ImageResult
                  key={`image-${jobId ?? idx}`}
                  isStreaming={isActivelyStreaming}
                  sessionId={fileMeta.sessionId || undefined}
                  slotTurnId={stableArtifactMessageId || undefined}
                  turnId={inlineImageTurnId}
                  activity={{
                    type: ActivityType.IMAGE_GENERATED,
                    job_id: jobId,
                    image_url: (activity.details?.image_url as string) || "",
                    revised_prompt: activity.details?.revised_prompt as string | undefined,
                    prompt: (activity.details?.prompt as string) || "",
                    is_edit: activity.details?.is_edit as boolean | undefined,
                    progress: activity.details?.progress as number | undefined,
                    partial,
                    session_file: activity.details?.session_file as string | undefined,
                    model_id: activity.details?.model_id as string | undefined,
                    model_label: activity.details?.model_label as string | undefined,
                  }}
                  onOpenInEditor={studioAiRoute ? handleOpenInEditor : undefined}
                />
              )
            })}
          </section>
        )}

        {!isActivelyStreaming && (
          <Citation
            ref={citationRef}
            answer={systemProps.answer as ChatAppResponse}
            citations={parsedAnswer.citations}
            citationSection={citationSection}
            documents={documents ?? []}
            messageIndex={index}
          />
        )}

        {fileTerminalState}

        {!!followupQuestions?.length && showFollowupQuestions && (
          <FollowupQuestions
            followupQuestions={followupQuestions}
            onClick={(question) => {
              if (onFollowupQuestionClicked) {
                onFollowupQuestionClicked(question)
              }
            }}
          />
        )}
      </SystemMessageLayout.Content>
      {systemProps.answer.completed && (
        <>
          <CardFooter
            data-testid="system-message-actions"
            className={cn(
              "flex w-full flex-wrap items-center gap-2 pb-0 pl-11 group-hover:visible lg:invisible",
              {
                "lg:visible": isLastMessage,
              }
            )}
          >
            <div className="flex flex-row flex-wrap items-center gap-1">
              <SystemMessageActions
                answer={systemProps.answer}
                citationSection={citationSection}
                parsedAnswer={parsedAnswer}
                sessionId={fileMeta.sessionId || systemProps.sessionId}
                sessionType={sessionType}
                showCopy={!isPersonalMessage}
                showCopyRaw={showCopyRaw && !isPersonalMessage}
                showCreateDocument={!isReader && !isPersonalMessage}
                showDownload={showDownload && !isPersonalMessage}
                showFactCheck={showFactCheck && !isPersonalMessage}
                showPublishArtifact={showPublishArtifact && !isPersonalMessage}
                onCopyRawToClipboardClicked={handleCopyRawToClipboardClicked}
                onCopyToClipboardClicked={handleCopyToClipboardClicked}
                onCreateDocumentClicked={handleCreateDocumentFromResponse}
                onDownloadClicked={handleDownloadClicked}
                onPublishArtifactClicked={onPublishArtifactClicked}
              />
              {/* Surface-specific actions belong on the SAME row as the icons
                  and "Fact check" — a second row of buttons under the answer
                  reads as a separate offer (#3165). */}
              {children}
              {hasDropdownActions && (
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger
                        aria-label={t("answer.more_actions")}
                        className={cn(
                          buttonVariants({
                            size: "icon",
                            variant: "icon",
                            className: "size-7",
                          })
                        )}
                      >
                        <MoreHorizontal aria-hidden="true" className="size-5" />
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                      <p>{t("answer.actions.menu_tooltip", "More")}</p>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end">
                    {quickActions.map((action) => (
                      <DropdownMenuItem
                        key={action.key}
                        className="gap-2"
                        onSelect={(event) => {
                          event.preventDefault()
                          action.onSelect()
                        }}
                      >
                        <span className="flex items-center gap-2">
                          {action.icon}
                          <span>{action.label}</span>
                        </span>
                      </DropdownMenuItem>
                    ))}
                    {showAnalysisPanelIcon && (
                      <DropdownMenuItem
                        className="gap-2"
                        disabled={!hasDataPoints(systemProps.answer)}
                        onSelect={(event) => {
                          event.preventDefault()
                          analysisTriggerRef.current?.click()
                        }}
                      >
                        <Lightbulb className="size-4" />
                        <span>{t("answer.show_analysis")}</span>
                      </DropdownMenuItem>
                    )}
                    {(quickActions.length > 0 || showAnalysisPanelIcon) && (
                      <DropdownMenuSeparator />
                    )}
                    <DropdownMenuItem
                      className="gap-2"
                      onSelect={(event) => {
                        event.preventDefault()
                        setIsResponseIssueDialogOpen(true)
                      }}
                    >
                      <Bug className="size-4" />
                      <span>{t("answer.reportIssue", { defaultValue: "Report issue" })}</span>
                    </DropdownMenuItem>
                    {onBreakOutChat && (
                      <>
                        {!(quickActions.length > 0 || showAnalysisPanelIcon) && (
                          <DropdownMenuSeparator />
                        )}
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={(event) => {
                            event.preventDefault()
                            const renderedText = answerTextRef.current?.innerText || messageContent
                            onBreakOutChat(renderedText)
                          }}
                        >
                          <ArrowUpRight className="size-4" />
                          <span>{t("answer.break_out_to_chat")}</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    {onSwitchModel && availableModels.length > 0 && (
                      <>
                        {(quickActions.length > 0 ||
                          showAnalysisPanelIcon ||
                          Boolean(onBreakOutChat)) && <DropdownMenuSeparator />}
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="gap-2">
                            <SlidersHorizontal className="size-4" />
                            <span>
                              {t("answer.actions.switch_model", { defaultValue: "Switch model" })}
                            </span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {availableModels.map((model) => (
                              <DropdownMenuItem
                                key={model.id}
                                className="gap-2"
                                onSelect={(event) => {
                                  event.preventDefault()
                                  onSwitchModel(model.id)
                                }}
                              >
                                <span
                                  className={cn(
                                    "font-medium",
                                    model.id === currentModelId ? "text-primary" : undefined
                                  )}
                                >
                                  {model.display_name}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </>
                    )}
                    {shareMessageHandler && (
                      <>
                        {(quickActions.length > 0 ||
                          showAnalysisPanelIcon ||
                          Boolean(onBreakOutChat) ||
                          (Boolean(onSwitchModel) && availableModels.length > 0)) && (
                          <DropdownMenuSeparator />
                        )}
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={(event) => {
                            event.preventDefault()
                            shareMessageHandler(systemProps.messageId)
                          }}
                        >
                          <Share2 className="size-4" />
                          <span>
                            {t("answer.share_message", { defaultValue: "Share message" })}
                          </span>
                        </DropdownMenuItem>
                      </>
                    )}
                    {messageContent && !isPersonalMessage && (
                      <>
                        {(quickActions.length > 0 ||
                          showAnalysisPanelIcon ||
                          Boolean(onBreakOutChat) ||
                          Boolean(shareMessageHandler) ||
                          (Boolean(onSwitchModel) && availableModels.length > 0)) && (
                          <DropdownMenuSeparator />
                        )}
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={(event) => {
                            event.preventDefault()
                            void handleAddToMemory()
                          }}
                        >
                          <Brain className="size-4" />
                          <span>Add to memory</span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <FeedbackDialog
              hideTrigger
              lockLane
              initialLane="bug"
              open={isResponseIssueDialogOpen}
              prefillContext={{
                messageId: messageId || null,
                sessionId: fileMeta.sessionId || systemProps.sessionId || null,
                responseContent: messageContent || null,
              }}
              onOpenChange={setIsResponseIssueDialogOpen}
            />
            {workflowTitle && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-muted/60 text-muted-foreground ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-medium sm:gap-1 sm:px-3 sm:py-1 sm:text-xs">
                    <Workflow className="size-3" />
                    <span className="max-w-[80px] truncate sm:max-w-none">{workflowTitle}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                  <p className="text-xs font-medium">
                    {t("answer.workflow_tooltip", "Flow used for this response")}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {(modelInfo.label || modelInfo.effortLevel) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      "bg-muted/60 text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium sm:gap-1.5 sm:px-3 sm:py-1 sm:text-xs",
                      !workflowTitle && "ml-auto"
                    )}
                  >
                    {modelInfo.label && (
                      <span className="max-w-[60px] truncate sm:max-w-none">{modelInfo.label}</span>
                    )}
                    {modelInfo.effortLevel && (
                      <>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="capitalize">{modelInfo.effortLevel}</span>
                      </>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                  <p className="text-xs font-medium">
                    {modelInfo.effortLevel
                      ? t("answer.model_effort_tooltip", {
                          defaultValue: "{{model}} · reasoning effort: {{effort}}",
                          model: modelInfo.label || "AI",
                          effort: modelInfo.effortLevel,
                        })
                      : t("answer.model_tooltip", "Model used for this response")}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </CardFooter>

          {showAnalysisPanelIcon && (
            <AnalysisPanel
              citationSection={citationSection}
              answer={
                {
                  ...systemProps.answer,
                  context: getResponseContext(systemProps.answer),
                } as ChatAppResponse
              }
            >
              <button ref={analysisTriggerRef} aria-hidden="true" className="hidden" />
            </AnalysisPanel>
          )}
        </>
      )}
    </SystemMessageLayout>
  )
}

/**
 * A turn that ended in a failure, rendered instead of an answer.
 *
 * Some errors (e.g. `FileUploadError` for an unreadable attachment, or a
 * hosted agent's refusal) persist an i18n hint, because the message itself was
 * composed somewhere with no locale — the reader's language wins whenever a
 * translation exists, and the raw message is the fallback.
 */
function SystemMessageErrorView({
  answer,
  sessionId,
}: {
  answer: ChatAppResponse | ChatAppResponseOrError
  sessionId?: string
}) {
  const { t } = useTranslation()
  const errorMessage = ("error" in answer && answer.error) || "Unknown error"
  const displayError = answer.translation_key
    ? t(answer.translation_key, { ...answer.translation_params, defaultValue: errorMessage })
    : errorMessage
  return (
    <Error
      canContinue={false}
      error={displayError}
      errorCode={answer.error_code}
      errorId={answer.error_id}
      sessionId={sessionId}
    />
  )
}

function SystemMessageWithBoundary({ ...props }: SystemMessageProps) {
  const { messageId } = props
  // The failure branch lives HERE, not inside `SystemMessageInner`, because
  // `SystemMessageInner` calls ~30 more hooks after the point the error check
  // used to return from. A message that failed AFTER it had already rendered —
  // a hosted agent's run settling failed mid-stream — therefore changed its
  // hook count in place, and React tore the message down with "Should have a
  // queue. You are likely calling Hooks conditionally", so the thread showed
  // the untranslated "This message couldn't be rendered. Please retry." card
  // INSTEAD of the explanation the failure had carefully localized. Deciding
  // one level up unmounts the inner tree, which is a legal remount.
  if ("error" in props.answer) {
    return <SystemMessageErrorView answer={props.answer} sessionId={props.sessionId} />
  }
  return (
    <SystemMessageBoundary resetKey={messageId}>
      <SystemMessageInner {...props} />
    </SystemMessageBoundary>
  )
}

export const SystemMessage = memo(SystemMessageWithBoundary)
